import { useAuth } from "@/hooks/use-auth-simple";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { ArrowLeft, Save } from "lucide-react";
import { Link, Redirect, useParams } from "wouter";
import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";

type SafeUser = {
  id: string;
  username: string;
  isAdmin?: boolean;
  is_admin?: boolean;
  department?: string;
};

// Sample guideline sections - will be replaced with database later
const guidelineSections: Record<string, { title: string; sections: { number: string; title: string; content: string }[] }> = {
  'tls-flex-dscr-non-qm': {
    title: 'TLS FLEX DSCR NON-QM GUIDELINES',
    sections: [
      { number: '100', title: 'Fair Lending Statement', content: '' },
      { number: '200', title: 'Loan Programs', content: '' },
      { number: '201', title: 'Flex DSCR Non-QM Program Eligibility Matrix1', content: '' },
      { number: '202', title: 'Prepayment Penalty', content: '' },
      { number: '203', title: 'Loan Products', content: '' },
      { number: '204', title: 'State Licensing Requirements', content: '' },
      { number: '300', title: 'Borrower Eligibility', content: '' },
      { number: '301', title: 'Non-U.S. Citizen Eligibility', content: '' },
      { number: '302', title: 'Multiple Loans to the Same Borrower', content: '' },
      { number: '303', title: 'Multiple Financed Properties', content: '' },
      { number: '304', title: 'Pending Litigation', content: '' },
      { number: '305', title: 'First-Time Homebuyer', content: '' },
      { number: '306', title: 'First-Time Investor', content: '' },
      { number: '307', title: 'Manual Underwrite', content: '' },
      { number: '308', title: 'Exclusionary Lists', content: '' },
      { number: '400', title: 'Occupancy Eligibility', content: '' },
      { number: '401', title: 'Investment Property', content: '' },
      { number: '500', title: 'Credit Assessment', content: '' },
      { number: '501', title: 'Credit History', content: '' },
      { number: '502', title: 'Significant Derogatory Credit Events', content: '' },
      { number: '503', title: 'Judgements, Liens, Charge-Offs, Collections', content: '' },
      { number: '504', title: 'Fraud Alert', content: '' },
      { number: '505', title: 'Credit Freeze', content: '' },
      { number: '506', title: 'Housing Payment History', content: '' },
      { number: '600', title: 'Liability Assessment', content: '' },
      { number: '700', title: 'Income Assessment', content: '' },
      { number: '701', title: 'Overview', content: '' },
      { number: '702', title: 'Debt Service Coverage Ratio (DSCR)', content: '' },
      { number: '800', title: 'Asset Assessment', content: '' },
      { number: '801', title: 'General Asset Requirements', content: '' },
      { number: '802', title: 'Depository Accounts', content: '' },
      { number: '803', title: 'Earnest Money Deposit (EMD)', content: '' },
      { number: '804', title: 'Stocks, Bonds, and Mutual Funds', content: '' },
      { number: '805', title: 'Retirement Funds', content: '' },
      { number: '806', title: 'Anticipated Sales Proceeds', content: '' },
      { number: '807', title: 'Gift Funds', content: '' },
      { number: '808', title: 'Minimum Borrower Contribution', content: '' },
      { number: '809', title: 'Cash Value of Life Insurance', content: '' },
      { number: '810', title: 'Trust Accounts', content: '' },
      { number: '811', title: 'Business Assets', content: '' },
      { number: '812', title: 'Interested Party Contributions (IPC)', content: '' },
      { number: '813', title: 'Reserves', content: '' },
      { number: '900', title: 'Mortgage Eligibility', content: '' },
      { number: '901', title: 'Escrows/Impound Waivers', content: '' },
      { number: '902', title: 'Purchase Transactions', content: '' },
      { number: '903', title: 'Property Flip Transactions', content: '' },
      { number: '904', title: 'Properties Recently Listed for Sale', content: '' },
      { number: '905', title: 'Short Sales and Short Payoffs', content: '' },
      { number: '906', title: 'Refinance Transactions', content: '' },
      { number: '907', title: 'Continuity of Obligation', content: '' },
      { number: '908', title: 'Rate/Term Refinance', content: '' },
      { number: '909', title: 'Cash-Out Refinance', content: '' },
      { number: '910', title: 'Adding/Removing Borrowers', content: '' },
      { number: '911', title: 'Net Tangible Benefit (NTB)', content: '' },
      { number: '912', title: 'Non-Arm\'s Length Transactions', content: '' },
      { number: '913', title: 'Subordinate Financing', content: '' },
      { number: '914', title: 'Age-Related Deed Restriction', content: '' },
      { number: '915', title: 'Ineligible Transactions', content: '' },
      { number: '916', title: 'Geographic Eligibility', content: '' },
      { number: '917', title: 'Multiple Parcels', content: '' },
      { number: '918', title: 'Geographic Eligibility', content: '' },
      { number: '919', title: 'Multiple Parcels', content: '' },
      { number: '920', title: 'Condominium Projects', content: '' },
      { number: '921', title: 'PUD Projects', content: '' },
      { number: '922', title: 'Property Insurance Requirements', content: '' },
      { number: '923', title: 'Solar Panels', content: '' },
      { number: '924', title: 'Initial Disclosures', content: '' },
      { number: '925', title: 'Closing Disclosures', content: '' },
      { number: '926', title: 'Non-Borrowing Owner', content: '' },
      { number: '927', title: 'Affiliated Business Arrangements', content: '' },
      { number: '928', title: 'Fraud Detection', content: '' },
      { number: '929', title: 'Higher-Priced Mortgage Loans (HPML)', content: '' },
      { number: '1000', title: 'Title Insurance Policy Requirements', content: '' },
      { number: '1001', title: 'Overview', content: '' },
      { number: '1002', title: 'Title Vesting Requirements', content: '' },
      { number: '1003', title: 'Living (Inter Vivos) Revocable Trusts', content: '' },
      { number: '1004', title: 'Title Exceptions and Impediments', content: '' },
      { number: '1005', title: 'Leasehold Estate', content: '' },
      { number: '1006', title: 'Survey Requirements', content: '' },
      { number: '1007', title: 'Closing Protection Letter', content: '' },
      { number: '1008', title: 'Requirements for Use of a Power of Attorney', content: '' },
      { number: '1100', title: 'Appraisal Analysis', content: '' },
      { number: '1101', title: 'CDA', content: '' },
      { number: '1102', title: 'Second Appraisal', content: '' },
      { number: '1103', title: 'Appraisal Independence Requirements', content: '' },
      { number: '1104', title: 'Electronic Appraisal Review', content: '' },
      { number: '1105', title: 'Appraisal Report', content: '' },
      { number: '1106', title: 'Appraisal Transfers', content: '' },
      { number: '1107', title: 'Age of Appraisal Report', content: '' },
      { number: '1108', title: 'Purchase Transactions', content: '' },
      { number: '1109', title: 'Declining Markets', content: '' },
      { number: '1110', title: 'Field Review', content: '' },
      { number: '1111', title: 'Site Analysis', content: '' },
      { number: '1112', title: 'Improvements', content: '' },
      { number: '1113', title: 'Final Inspection and Completion Report', content: '' },
      { number: '1114', title: 'Disaster Policy', content: '' },
      { number: '1200', title: 'Texas 50(a)(6)', content: '' },
      { number: '1300', title: 'Texas 50(f)(2)', content: '' }
    ]
  }
};

export default function GuidelineDetailPage() {
  const { user, isLoading } = useAuth();
  const params = useParams();
  const guidelineId = params.id;
  const { toast } = useToast();
  
  const guideline = guidelineId ? guidelineSections[guidelineId] : null;
  const [sectionContents, setSectionContents] = useState<Record<string, string>>({});
  const [hasChanges, setHasChanges] = useState(false);

  useEffect(() => {
    if (guideline) {
      // Initialize section contents from guideline data
      const initialContents: Record<string, string> = {};
      guideline.sections.forEach(section => {
        initialContents[section.number] = section.content || '';
      });
      setSectionContents(initialContents);
    }
  }, [guideline]);

  const handleContentChange = (sectionNumber: string, content: string) => {
    setSectionContents(prev => ({
      ...prev,
      [sectionNumber]: content
    }));
    setHasChanges(true);
  };

  const handleSave = () => {
    // TODO: Implement API call to save section contents
    toast({
      title: "Saved",
      description: "Guideline sections have been saved successfully.",
    });
    setHasChanges(false);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#1a1a1a]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-100"></div>
      </div>
    );
  }

  if (!user) {
    return <Redirect to="/auth" />;
  }

  if (!(user?.isAdmin || (user as any)?.is_admin)) {
    return <Redirect to="/" />;
  }

  if (!guideline) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] text-gray-100 flex items-center justify-center">
        <Card className="rounded-lg border border-[#3a3a3a] bg-[#2a2a2a] p-8 text-center">
          <h3 className="text-xl font-semibold text-gray-300 mb-2">Guideline Not Found</h3>
          <p className="text-gray-500 mb-4">The guideline you are looking for does not exist.</p>
          <Link href="/guidelines">
            <Button variant="outline" className="border border-[#3a3a3a] text-gray-300 hover:bg-[#3a3a3a]">
              <ArrowLeft className="h-4 w-4 mr-2" /> Back to Guidelines
            </Button>
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-[#1a1a1a] text-gray-100" 
      style={{ 
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", "Helvetica", "Arial", sans-serif',
        paddingTop: 'env(safe-area-inset-top, 0px)',
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        paddingLeft: 'env(safe-area-inset-left, 0px)',
        paddingRight: 'env(safe-area-inset-right, 0px)'
      }}
    >
      {/* Header */}
      <header 
        className="px-4 sm:px-6 h-14 border-b border-[#3a3a3a] bg-[#2a2a2a] sticky top-0 z-50"
        style={{ 
          paddingTop: 'calc(14px + env(safe-area-inset-top, 0px))',
          height: 'calc(56px + env(safe-area-inset-top, 0px))',
          paddingLeft: 'calc(16px + env(safe-area-inset-left, 0px))',
          paddingRight: 'calc(16px + env(safe-area-inset-right, 0px))'
        }}
      >
        <div className="max-w-7xl mx-auto flex justify-between items-center h-full">
          <div className="flex items-center gap-4" style={{ textAlign: 'left' }}>
            <Link href="/guidelines">
              <Button 
                variant="ghost" 
                size="sm" 
                className="transition-all duration-200 rounded-md px-3 text-gray-300 hover:text-gray-100 hover:bg-[#3a3a3a] border border-[#3a3a3a] touch-manipulation"
                style={{ minHeight: '44px', fontSize: '16px', textAlign: 'center' }}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Back to Guidelines</span>
                <span className="sm:hidden">Back</span>
              </Button>
            </Link>
            <div className="border-l border-[#3a3a3a] pl-4 h-10 flex items-center">
              <div className="flex items-center gap-2 text-lg font-semibold text-gray-100" style={{ textAlign: 'left' }}>
                <span style={{ textAlign: 'left' }}>{guideline.title}</span>
              </div>
            </div>
          </div>
          {hasChanges && (
            <Button
              onClick={handleSave}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </Button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8" style={{ textAlign: 'left' }}>
        <div className="mb-6">
          <p className="text-gray-400 text-sm" style={{ textAlign: 'left', lineHeight: '1.6' }}>
            Edit guideline sections below. Department heads can update content directly in each section.
          </p>
        </div>

        {/* Sections */}
        <div className="space-y-6">
          {guideline.sections.map((section) => (
            <Card key={section.number} className="border-[#3a3a3a] bg-[#2a2a2a]">
              <CardHeader className="pb-3">
                <CardTitle className="text-gray-100 text-lg font-semibold" style={{ textAlign: 'left' }}>
                  {section.number}. {section.title}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Textarea
                  value={sectionContents[section.number] || ''}
                  onChange={(e) => handleContentChange(section.number, e.target.value)}
                  placeholder={`Enter content for ${section.number}. ${section.title}...`}
                  className="bg-[#1a1a1a] border-[#3a3a3a] text-gray-100 min-h-[200px] w-full resize-y"
                  style={{ fontFamily: 'monospace', fontSize: '14px' }}
                />
              </CardContent>
            </Card>
          ))}
        </div>
      </main>
    </div>
  );
}

